import useSWR from 'swr';

import {
  fetchAlreadyRecorded,
  fetchSummary,
  type LedgerTotal,
  type SummaryState,
} from '../data/reconciliationRepository.js';

export type ReconciliationData = Readonly<{
  alreadyRecorded: readonly LedgerTotal[];
  summary: SummaryState | null;
}>;

export type ReconciliationState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string; retry: () => void }
  | { status: 'ready'; data: ReconciliationData; refresh: () => Promise<unknown> };

/**
 * Everything one account-and-month reconciliation needs: what the ledger already holds, and
 * the draft (or confirmed record) if this month was started before.
 *
 * Both reads travel in the cache key, so switching month and coming back is instant and the
 * screen never has to copy fetched data into state by hand.
 */
export function useReconciliation(
  accountId: string | undefined,
  year: number,
  month: number,
): ReconciliationState {
  const key = accountId === undefined ? null : `reconciliation:${accountId}:${year}-${month}`;

  const { data, error, isLoading, mutate } = useSWR<ReconciliationData, Error>(
    key,
    async () => {
      const [alreadyRecorded, summary] = await Promise.all([
        fetchAlreadyRecorded(accountId!, year, month),
        fetchSummary(accountId!, year, month),
      ]);
      return { alreadyRecorded, summary };
    },
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  if (key === null) return { status: 'idle' };
  if (error) {
    return {
      status: 'error',
      message: error.message || 'Không đọc được số liệu tháng này.',
      retry: () => {
        void mutate();
      },
    };
  }
  if (isLoading || data === undefined) return { status: 'loading' };

  return { status: 'ready', data, refresh: () => mutate() };
}
